// Complete AuthorityOS item bank — verbatim per spec §1.
// Steps 1–5: Habits (Likert 1–5). Steps 6–10: Traits (check-all-that-apply with hidden levels). Step 11: Effect (Likert 1–5, 5 sub-groups).

export const LIKERT_LABELS = ["Strongly disagree", "Disagree", "Neutral", "Agree", "Strongly agree"];

export const HABIT_STEPS = [
  {
    step: 1,
    zone: "Space",
    items: [
      "I keep my space clean.",
      "I never walk past disorder; I act on it immediately.",
      "My workspace is clear and tidy.",
      "I keep my car free of unnecessary things.",
      "I have routines that maintain order.",
      "My digital ecosystem is organized (files, desktop, storage).",
      "I keep order under stress; I don't put things off until tomorrow.",
      "I inspire others through the way I keep order.",
      "I prioritize tasks that benefit my future self.",
    ],
  },
  {
    step: 2,
    zone: "Structure",
    items: [
      "I stick to a schedule.",
      "I plan my week in advance.",
      "I set goals and break them into milestones.",
      "I review my goals and milestones regularly.",
      "I check my calendar in the morning before I start.",
      "I say \"no\" to low-priority tasks.",
      "I set clear priorities and work through them in order.",
      "I avoid feeling overwhelmed by keeping clear priorities.",
      "I stay calm even with tasks I dislike.",
      "I stay calm during busy, stressful periods.",
    ],
  },
  {
    step: 3,
    zone: "Style",
    items: [
      "I eat healthily, even when I crave something else.",
      "I keep a daily hygiene routine.",
      "My appearance matches the respected authorities in my environment.",
      "You can tell from looking at me that I eat healthily.",
      "I dress and present myself like a respected authority.",
      "I move with self-assurance and intention, even under pressure.",
    ],
  },
  {
    step: 4,
    zone: "Relationships",
    items: [
      "I can strike up a conversation with strangers.",
      "I can talk with people of higher status.",
      "I make new friendships through in-person encounters.",
      "I can admit when I'm wrong.",
      "I accept compliments with composure.",
      "I can introduce people to each other.",
      "I can tell a good story.",
      "I can make small talk to build trust.",
      "I can ask a neighbor to turn the music down without it turning into an argument.",
      "I can make a stranger laugh.",
      "I can be vulnerable and open in conversations.",
      "I can raise difficult topics with people close to me.",
      "I can have difficult conversations at work.",
      "I stay present in conversations instead of drifting off in thought.",
      "I can give directions when it's appropriate.",
      "I can ask a stranger for a favor.",
      "I'm able to lead a group of strangers.",
    ],
  },
  {
    step: 5,
    zone: "Finances",
    items: [
      "I manage my finances and plan for the future.",
      "I align my spending with my future well-being.",
      "I resist impulse purchases.",
      "I pay bills on time or early.",
      "A bank would rate my creditworthiness highly.",
      "I stay away from unnecessary debt.",
    ],
  },
];

// TRAIT items — each carries a hidden level 1-5. NEVER expose the level in UI.
export const TRAIT_STEPS = [
  {
    step: 6,
    trait: "Self-Assurance",
    items: [
      { text: "I avoid conversations with strangers.", level: 1 },
      { text: "I over-analyze what people say.", level: 3 },
      { text: "My self-assurance is contagious to others.", level: 5 },
      { text: "I give in quickly during disagreements.", level: 2 },
      { text: "I can disagree tactfully while keeping harmony.", level: 4 },
      { text: "I take criticism very personally.", level: 1 },
      { text: "My self-worth is measured by external factors.", level: 3 },
      { text: "I can talk with anyone, anytime.", level: 5 },
      { text: "I frequently compare myself to others.", level: 2 },
      { text: "I act without much hesitation.", level: 4 },
      { text: "I change myself to please others.", level: 1 },
      { text: "My self-assurance comes from others' validation.", level: 3 },
      { text: "I need no external validation.", level: 5 },
      { text: "I regularly doubt my own judgment.", level: 2 },
      { text: "I thank people for respectful criticism.", level: 4 },
      { text: "I often have paralyzing indecision.", level: 1 },
      { text: "I can start conversations with strangers when the circumstances are right.", level: 3 },
      { text: "Others imitate my behavior and qualities.", level: 5 },
      { text: "I give up on goals after setbacks.", level: 2 },
      { text: "I stay fully present in conversations.", level: 4 },
    ],
  },
  {
    step: 7,
    trait: "Discipline",
    items: [
      { text: "I neglect daily tasks.", level: 1 },
      { text: "I neglect tasks that aren't \"fun.\"", level: 3 },
      { text: "I form new habits effortlessly.", level: 5 },
      { text: "I only do what interests me.", level: 2 },
      { text: "I get up as planned.", level: 4 },
      { text: "I can barely form new habits.", level: 1 },
      { text: "I'm frequently distracted by screens.", level: 3 },
      { text: "I complete tasks regardless of whether they're fun.", level: 5 },
      { text: "I set goals and then neglect them.", level: 2 },
      { text: "I follow my schedule and plan ahead.", level: 4 },
      { text: "I can't prioritize my time.", level: 1 },
      { text: "I usually make sensible choices about diet and fitness.", level: 3 },
      { text: "I almost always put goals above pleasure.", level: 5 },
      { text: "I start new habits and give up again.", level: 2 },
      { text: "I can say \"no\" when priorities don't fit.", level: 4 },
      { text: "I can't keep my living space clean.", level: 1 },
      { text: "I set goals deliberately and plan them out.", level: 3 },
      { text: "My discipline is contagious to others.", level: 5 },
      { text: "I have impulsive buying behavior.", level: 2 },
      { text: "I'm financially responsible and plan my finances.", level: 4 },
    ],
  },
  {
    step: 8,
    trait: "Initiative",
    items: [
      { text: "I take credit for others' successes.", level: 1 },
      { text: "Others feel comfortable in my presence.", level: 3 },
      { text: "I lead by good example for everyone.", level: 5 },
      { text: "I exaggerate my own contributions.", level: 2 },
      { text: "I inspire others through my behavior.", level: 4 },
      { text: "I blame circumstances for my life.", level: 1 },
      { text: "I communicate clearly.", level: 3 },
      { text: "I never claim team successes for myself alone.", level: 5 },
      { text: "I blame circumstances for my failures.", level: 2 },
      { text: "I pass on credit whenever possible.", level: 4 },
      { text: "I have poor emotional control and tend toward outbursts.", level: 1 },
      { text: "I can usually manage stress.", level: 3 },
      { text: "I always look for the best in others.", level: 5 },
      { text: "My behavior changes drastically in stressful situations.", level: 2 },
      { text: "I take a genuine interest in others' growth.", level: 4 },
      { text: "I belittle others to feel better about myself.", level: 1 },
      { text: "I sometimes let stress spill over onto others.", level: 3 },
      { text: "My composure automatically creates trust.", level: 5 },
      { text: "I care little about others' development.", level: 2 },
      { text: "I don't tend toward outbursts under stress.", level: 4 },
    ],
  },
  {
    step: 9,
    trait: "Appreciation",
    items: [
      { text: "I mostly see the negative.", level: 1 },
      { text: "I express thanks when it's needed.", level: 3 },
      { text: "My appreciation is habitual and deep.", level: 5 },
      { text: "I'm easily overwhelmed by stress.", level: 2 },
      { text: "I feel appreciation for small things throughout the day.", level: 4 },
      { text: "I can barely express appreciation.", level: 1 },
      { text: "I mostly see the positive side.", level: 3 },
      { text: "I appreciate things where others turn bitter.", level: 5 },
      { text: "I have a slight sense of entitlement.", level: 2 },
      { text: "I encourage others to recognize their good fortune.", level: 4 },
      { text: "I see the negative where others are grateful.", level: 1 },
      { text: "I feel appreciation when I'm reminded of it.", level: 3 },
      { text: "My sense of appreciation is contagious.", level: 5 },
      { text: "I worry even when things are going well.", level: 2 },
      { text: "I make a conscious effort to see the best in others.", level: 4 },
      { text: "I think grateful people are only pretending.", level: 1 },
      { text: "I can feel happy for others.", level: 3 },
      { text: "I create an environment where others become grateful.", level: 5 },
      { text: "I rarely express appreciation.", level: 2 },
      { text: "I can find something positive even in negative events.", level: 4 },
    ],
  },
  {
    step: 10,
    trait: "Vitality",
    items: [
      { text: "I cope poorly with stress.", level: 1 },
      { text: "I enjoy situations as long as the conditions are right.", level: 3 },
      { text: "I stay confident even in difficult phases.", level: 5 },
      { text: "I frequently have self-doubt.", level: 2 },
      { text: "I don't need an external event to enjoy the moment.", level: 4 },
      { text: "I frequently criticize others.", level: 1 },
      { text: "The outside world decides whether I have a \"good day.\"", level: 3 },
      { text: "I'm the \"rock\" in stressful situations.", level: 5 },
      { text: "I feel that something is working against me.", level: 2 },
      { text: "I encourage others to feel good.", level: 4 },
      { text: "My behavior has a negative effect on my environment.", level: 1 },
      { text: "I avoid negative discussions.", level: 3 },
      { text: "My well-being is contagious.", level: 5 },
      { text: "I have a negative effect on others when I'm stressed.", level: 2 },
      { text: "I complain only when it's absolutely necessary.", level: 4 },
      { text: "I set goals to avoid pain.", level: 1 },
      { text: "I can usually overcome negative thoughts.", level: 3 },
      { text: "My mere presence does others good.", level: 5 },
      { text: "I only persevere as long as tasks are fun.", level: 2 },
      { text: "I have a positive effect on others even in stressful situations.", level: 4 },
    ],
  },
];

// Step 11: Effect — 5 sub-groups, Likert 1–5
export const EFFECT_STEP = {
  step: 11,
  markers: [
    {
      marker: "Body Language",
      items: [
        "I move slowly and deliberately, not hastily.",
        "I act without hesitation or restrained gestures.",
        "My blink is calm, not rapid.",
        "My walk conveys conviction and clarity.",
        "My hands, arms, and legs look relaxed, not tense.",
      ],
    },
    {
      marker: "Appearance",
      items: [
        "I hold an upright, relaxed posture.",
        "My appearance is well-groomed (skin, teeth, hygiene).",
        "My clothing is appropriate, clean, and wrinkle-free.",
        "My shoulders are loose, not raised.",
        "I avoid fashion hypes — my style stays timeless.",
      ],
    },
    {
      marker: "Aura",
      items: [
        "My body looks relaxed, not tense.",
        "Others feel comfortable in my presence.",
        "My face shows a baseline mood of well-being.",
        "I avoid frequent face-touching and nervous movements.",
        "I'm physically open in conversations, not closed off.",
      ],
    },
    {
      marker: "Presence",
      items: [
        "I stay fully present in conversations; my mind doesn't wander.",
        "My eye contact is genuine, not forced.",
        "I truly listen instead of already planning my reply.",
        "I remember what people tell me.",
        "I respond genuinely to the emotional high points of others' stories.",
      ],
    },
    {
      marker: "Congruence",
      items: [
        "What I say and what I feel match.",
        "I come across as congruent — no contradiction between words and body.",
        "I need no mask; my inner self is my outer self.",
        "Even under stress, my baseline mood stays stable.",
        "I keep my promises — including to myself.",
      ],
    },
  ],
};

export const TOTAL_STEPS = 11;
export const HABIT_ZONES = ["Space", "Structure", "Style", "Relationships", "Finances"];
export const TRAITS = ["Self-Assurance", "Discipline", "Initiative", "Appreciation", "Vitality"];
export const EFFECT_MARKERS = ["Body Language", "Appearance", "Aura", "Presence", "Congruence"];

// Score reading guide (1.0–5.0)
export function readScore(v) {
  if (v < 2.0) return { label: "Very weak", tone: "deficit" };
  if (v < 2.5) return { label: "Weak", tone: "deficit" };
  if (v < 3.5) return { label: "Average", tone: "average" };
  if (v <= 4.0) return { label: "Solid", tone: "solid" };
  return { label: "Strong", tone: "strong" };
}
